import React, { useState, useMemo } from 'react';
import { DevelopmentProjectListItemDto, PortalProjectListItemDto } from 'src/api/v2';
import { ProjectPhaseTypes } from 'src/constants/project';
import ProjectPagination from './ProjectPagination';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import { useAuth } from 'src/features/authorization';
import AddProjectModal from '../AddProjectModal';
import useProjectsExport from '../../hooks/use-projects-export';
import {
  Alert,
  Button,
  Grid,
  GridItem,
  Heading,
  Icon,
  Label,
  Search,
  Select,
  SelectOption,
} from '@innovationnorway/cl-react';

type ProjectTypes = DevelopmentProjectListItemDto | PortalProjectListItemDto;

type ProjectListWrapperTypes<T = ProjectTypes> = {
  projects: T[];
  isFetching: boolean;
  hideCreateButton?: boolean;
  projectType?: 'development' | 'operation' | 'other';
};

type ProjectListWrapperWithTypeDeclaration<T = ProjectTypes> = React.FC<ProjectListWrapperTypes<T>>;

const ProjectListWrapper: <T extends ProjectTypes>(
  props: ProjectListWrapperTypes<T>,
) => React.ReactElement<ProjectListWrapperWithTypeDeclaration<T>> = ({
  projects,
  isFetching,
  hideCreateButton,
  projectType,
}) => {
  const [searchResults, setSearchResults] = useState<Array<ProjectTypes>>([]);
  const [selectedFilter, setSelectedFilter] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');

  const { t: tCommon } = useTranslation();
  const { t: tButtons } = useTranslation('buttons');
  const { t: tProjects } = useTranslation('projects');

  const showCreateModal = useBoolean();

  const { checkAccess } = useAuth();

  const { exportToExcel } = useProjectsExport();

  const searchInputHandler = (searchInput: string) => {
    setSearchResults(projects.filter((project) => project.name.toLowerCase().includes(searchInput)));
  };

  React.useEffect(() => {
    setSearchResults(projects);
  }, [projects]);

  const notificationText = useMemo(() => {
    if (projectType === 'development') {
      return tProjects('noInFinancedProjects');
    }

    if (projectType === 'other') {
      return tProjects('noOwnProjects');
    }

    return '';
  }, [projectType, tProjects]);

  const filterOptions = [
    { text: tCommon('none'), value: '' },
    { text: tCommon('active_plural'), value: 'active' },
    { text: tProjects('ended'), value: 'ended' },
  ];

  const filterResults = useMemo(() => {
    switch (selectedFilter) {
      case 'active':
        return searchResults.filter((result) => result.projectPhase?.id !== ProjectPhaseTypes.terminated.id);

      case 'ended':
        return searchResults.filter((result) => result.projectPhase?.id === ProjectPhaseTypes.terminated.id);

      default:
        return searchResults;
    }
  }, [searchResults, selectedFilter]);

  const handleOnExport = () => {
    exportToExcel(filterResults);
  };

  //TODO: Ordne mer filtrering/sortering
  return (
    <div className="stack">
      <Grid gap="s">
        <GridItem spanLarge={3} spanMedium={4} spanSmall={4}>
          <Label htmlFor="project-list-filter">{tCommon('filterBy')}</Label>
          <Select id="project-list-filter" name="filter" onChange={(e) => setSelectedFilter(e.target.value)}>
            {filterOptions.map((option) => (
              <SelectOption key={option.value} value={option.value}>
                {option.text}
              </SelectOption>
            ))}
          </Select>
        </GridItem>
        <GridItem spanLarge={9} spanMedium={8} spanSmall={4}>
          <Label htmlFor="project-list-search">{tCommon('search')}</Label>
          <Search>
            <Search.Input
              id="project-list-search"
              placeholder={tCommon('search')}
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                searchInputHandler(e.target.value.toLowerCase());
              }}
            />
            <Search.Clear />
          </Search>
        </GridItem>
      </Grid>
      <div className="display--flex justify-content--flex-end align-items--center gap--2">
        <Button type="button" variant="secondary" onClick={handleOnExport}>
          <Icon prefix="fasr" iconName="file-excel" />
          {tCommon('exportToExcel')}
        </Button>

        {checkAccess('PortalProject').write && hideCreateButton !== true ? (
          <Button type="button" variant="secondary" onClick={showCreateModal.setTrue}>
            <Icon prefix="fasr" iconName="plus" />
            {tButtons('createProject')}
          </Button>
        ) : null}
      </div>

      <Heading level={2}>
        {filterResults.length} {tCommon('hits')}
      </Heading>
      {projects.length === 0 ? (
        <Alert data-color="info">{notificationText}</Alert>
      ) : (
        <ProjectPagination projects={filterResults} isFetching={isFetching} projectType={projectType} />
      )}

      <AddProjectModal show={showCreateModal.value} onClose={showCreateModal.setFalse} />
    </div>
  );
};

export default ProjectListWrapper;
